import { useState } from "react";
import { useForm } from "react-hook-form";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import { betterAuthErrorText } from "lib/auth/authClient";
import { useVerifyBackupCode } from "lib/hooks/api/auth/useVerifyBackupCode";
import { useVerifyTotp } from "lib/hooks/api/auth/useVerifyTotp";
import { performLogout } from "lib/userUtils";
import { Checkbox } from "../../components/form_components/Inputs";
import Label from "../../components/ui/input/Label";
import { Card, Form, Page } from "./authPageStyles";
import { StotlesLogo } from "./StotlesLogo";

type FormValues = {
  code: string;
  trustDevice: boolean;
};

type Props = {
  // Called once a code verifies successfully and the full session is established.
  onVerified: () => void;
};

// Sign-in 2FA challenge. Rendered when an enrolled user signs in and better-auth responds with
// `twoFactorRedirect` (no full session yet). A valid TOTP code completes the sign-in; if the user
// can't produce one (lost authenticator) they can switch to a one-time backup code.
export function TwoFactorVerifyStep({ onVerified }: Props) {
  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: { code: "", trustDevice: false },
  });
  const [useBackupCode, setUseBackupCode] = useState(false);

  const totp = useVerifyTotp();
  const backup = useVerifyBackupCode();
  const active = useBackupCode ? backup : totp;

  const onSubmit = (values: FormValues) => {
    active.mutate(
      { code: values.code.trim(), trustDevice: values.trustDevice },
      { onSuccess: onVerified },
    );
  };

  return (
    <Page>
      <Card>
        <StotlesLogo />
        <Text h1>Two-factor authentication</Text>
        <Text color="sysTextSecondary">
          {useBackupCode
            ? "Enter one of your backup codes to finish signing in."
            : "Enter the 6-digit code from your authenticator app to finish signing in."}
        </Text>

        <Form onSubmit={handleSubmit((values) => onSubmit(values))}>
          <Flex flexDirection="column" gap={4}>
            <Label htmlFor="code">{useBackupCode ? "Backup code" : "Authentication code"}</Label>
            <Input
              name="code"
              control={control}
              type="text"
              placeholder={useBackupCode ? "Enter a backup code" : "123456"}
              // Just `required` (no 6-digit pattern): the same field accepts both a TOTP code and a
              // backup code, and the server validates the actual value either way.
              rules={{ required: { value: true, message: "A code is required" } }}
            />
          </Flex>

          <Checkbox
            name="trustDevice"
            control={control}
            label="Trusted device"
            fieldLabel="Remember this device for 30 days"
          />

          {active.isError && (
            <Text color="sysDestructiveDefault">
              {betterAuthErrorText(active.error, "Invalid code")}
            </Text>
          )}

          <Button buttonType="primary" type="submit" fullWidth loading={active.isLoading}>
            Verify
          </Button>
        </Form>

        <Button buttonType="tertiary" type="button" onClick={() => setUseBackupCode((v) => !v)}>
          {useBackupCode ? "Use your authenticator app instead" : "Use a backup code instead"}
        </Button>

        {/* The only way out of a half-finished sign-in. It used to live in the page header, which
            these pages no longer have, so it moves into the card. */}
        <Button buttonType="tertiary" type="button" onClick={performLogout}>
          Log out
        </Button>
      </Card>
    </Page>
  );
}
